import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ReactComponent as HubIcon } from '../../../../../images/icons/hub_icon.svg';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../../images/icons/update_sw_success.svg';
import { ReactComponent as UpdateSwFailedIcon } from '../../../../../images/icons/update_sw_failed.svg';
import NewModal from '../../../../Shared/NewModal';
import { dimGrey } from '../../../../../mainTheme';
import CustomerCareSelect from '../../../../Shared/CustomerCareSelect';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../../store';
import { SoftwareVersion } from '../../../../../store/software/softwareTypes';
import { isNull } from 'lodash';
import { getSoftwareVersions } from '../../../../../store/software/actionCreators';
import { DevicesStore } from '../../../../../store/devices/deviceTypes';
import {
  clearDeviceActionStatus,
  updateDeviceSoftware,
} from '../../../../../store/devices/actionCreators';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

interface UpdateSoftwareDialogProps {
  onClose: () => void;
  currentVersion: number;
  deviceId: number;
  deviceSocId: number;
  isFacilityAdmin?: boolean;
  isInternalPrivileged?: boolean;
}

interface StatusViewProps {
  icon: React.ReactElement;
  title: string;
  message: string;
}

const StatusView: React.FC<StatusViewProps> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center' }}>
      {message}
    </Typography>
  </Box>
);

export const UpdateSoftwareDialog: React.FC<UpdateSoftwareDialogProps> = ({
  onClose,
  currentVersion,
  deviceId,
  deviceSocId,
  isFacilityAdmin,
  isInternalPrivileged,
}) => {
  const { softwareVersions, isLoading, actionStatus } = useSelector<
    RootState,
    {
      softwareVersions: SoftwareVersion[] | null;
      isLoading: boolean;
      actionStatus: DevicesStore['actionStatus'];
    }
  >((state) => ({
    softwareVersions: state.software.softwareVersions,
    isLoading: state.software.isLoading,
    actionStatus: state.devices.actionStatus,
  }));

  const filteredSoftwareVersions = useMemo(() => {
    if (!softwareVersions) return [];
    return softwareVersions.filter((version) => version.soc_id === deviceSocId);
  }, [softwareVersions, deviceSocId]);

  const dispatch = useDispatch();
  const [step, setStep] = useState<'select' | 'success' | 'failed'>('select');
  const [selectedVersion, setSelectedVersion] =
    useState<number>(currentVersion);
  const [forceUpdate, setForceUpdate] = useState<boolean>(false);

  useEffect(() => {
    if (isNull(softwareVersions)) {
      dispatch(getSoftwareVersions(isFacilityAdmin ?? false));
    }
  }, [dispatch, softwareVersions, isFacilityAdmin]);

  useEffect(() => {
    if (actionStatus.type === 'software_update') {
      setStep(actionStatus.success ? 'success' : 'failed');
    }
  }, [actionStatus]);

  const handleUpdate = () => {
    const versionToUpdate = selectedVersion === 0 ? null : selectedVersion;
    dispatch(updateDeviceSoftware(deviceId, versionToUpdate, forceUpdate));
  };

  const handleReset = () => {
    dispatch(clearDeviceActionStatus());
    setStep('select');
    setSelectedVersion(currentVersion);
    setForceUpdate(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderSelectView = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HubIcon />
      <Typography sx={typography.heading}>Update software</Typography>
      <Typography sx={typography.subText}>
        Select the software version for the Hub.
      </Typography>
      <Box mt={2}>
        <CustomerCareSelect
          label=""
          value={selectedVersion}
          onChange={(e) =>
            setSelectedVersion(e.target.value as unknown as number)
          }
          fullWidth
        >
          {[{ id: 0, version: 'None', version_raw: 'None' }]
            .concat(filteredSoftwareVersions)
            .map((version) => (
              <MenuItem key={version.id} value={version.id}>
                {version.version_raw}
              </MenuItem>
            ))}
        </CustomerCareSelect>
      </Box>
      {isInternalPrivileged && (
        <FormControlLabel
          control={
            <Checkbox
              checked={forceUpdate}
              onChange={(e) => setForceUpdate(e.target.checked)}
            />
          }
          label="Force update (install immediately — the Hub cannot skip)"
        />
      )}
    </Box>
  );

  const renderContent = () => {
    switch (step) {
      case 'select':
        return renderSelectView();
      case 'success':
        return (
          <StatusView
            icon={<UpdateSwSuccessIcon />}
            title="Update successful"
            message="The update will finalize overnight."
          />
        );
      case 'failed':
        return (
          <StatusView
            icon={<UpdateSwFailedIcon />}
            title="Update failed"
            message="Please try again later."
          />
        );
    }
  };

  const actionConfig = {
    select: { text: 'Update', onClick: handleUpdate },
    success: { text: 'OK', onClick: handleClose },
    failed: { text: 'Try again', onClick: handleReset },
  };

  return (
    <NewModal
      showCloseX
      title="Update software"
      onClose={handleClose}
      maxWidth="xs"
      actions={{
        primaryAction: {
          ...actionConfig[step],
          disabled:
            step === 'select' &&
            selectedVersion === currentVersion &&
            !forceUpdate,
        },
        secondaryAction:
          step === 'select'
            ? {
                text: 'Cancel',
                onClick: handleClose,
              }
            : undefined,
      }}
    >
      {isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      ) : (
        renderContent()
      )}
    </NewModal>
  );
};
